<script setup lang="ts">
const { state: panelState } = usePanel()

type OptionType = "Edits" | "Metadata" | "Comments" | "Sharing"
const options: OptionType[] = [
  "Edits",
  "Metadata",
  "Comments",
  "Sharing",
]

function isSelected(option: OptionType) {
  switch (option) {
    case "Edits":
      return panelState.value.showHistoryEdits
    case "Metadata":
      return panelState.value.showHistoryMetadata
    case "Comments":
      return panelState.value.showHistoryComments
    case "Sharing":
      return panelState.value.showHistorySharing
  }
}

const selectedOptions = computed(() => {
  return options.filter(isSelected)
})

const selectedOptionsText = computed(() => {
  return selectedOptions.value.join(", ")
})

const allSelected = computed(() => {
  return selectedOptions.value.length === options.length
})

function handleSelect(option: OptionType, toggle: () => void) {
  if (selectedOptions.value.length === 1 && selectedOptions.value[0] === option)
    return console.warn("Cannot unselect the last option")

  switch (option) {
    case "Edits":
      panelState.value.showHistoryEdits = !panelState.value.showHistoryEdits
      break
    case "Metadata":
      panelState.value.showHistoryMetadata = !panelState.value.showHistoryMetadata
      break
    case "Comments":
      panelState.value.showHistoryComments = !panelState.value.showHistoryComments
      break
    case "Sharing":
      panelState.value.showHistorySharing = !panelState.value.showHistorySharing
      break
  }
  // TODO: handle select
  toggle()
}
</script>

<template>
  <dropdown
    anchor-x="right"
    anchor-y="bottom"
    flow-x="left"
    flow-y="down"
  >
    <template #default="{ toggle }">
      <panel-button @click="toggle()">
        <template #icon>
          <div v-if="allSelected">
            All
          </div>
          <div v-else>
            {{ selectedOptionsText }}
          </div>
          <icon-action
            size="xs"
            type="chevron-down"
            class="chevron"
          />
        </template>
      </panel-button>
    </template>
    <template #menu="{ toggle }">
      <dropdown-menu class="dropdown-menu">
        <div class="inline-select-dropdown">
          <div
            v-for="option in options"
            :key="option"
            class="inline-select-option group"
            @click="handleSelect(option, toggle)"
          >
            <icon-property
              v-if="option === 'Edits'"
              size="xs"
              type="file"
              class="icon"
            />
            <icon-property
              v-else-if="option === 'Metadata'"
              size="xs"
              type="metadata"
              class="icon"
            />
            <icon-property
              v-else-if="option === 'Comments'"
              size="xs"
              type="comment"
              class="icon"
            />
            <icon-property
              v-else-if="option === 'Sharing'"
              size="xs"
              type="user"
              class="icon"
            />
            <div>{{ option }}</div>
            <icon-action
              v-if="isSelected(option)"
              size="xs"
              type="check"
              class="check"
            />
          </div>
        </div>
      </dropdown-menu>
    </template>
  </dropdown>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.dropdown-menu
  @apply my-2

.chevron
  @apply -mr-1

.inline-select-dropdown
  @apply flex flex-col gap-[#{$menu-gap-items}] p-[#{$menu-p-default}]

.inline-select-option
  @apply flex items-center
  @apply h-5 -m-1 p-1 gap-2
  @apply t-menu-sm text-[#{$global-t-default}]
  @apply rounded-[#{$menu-br-item}]
  &:hover
    @apply cursor-pointer
    @apply bg-[#{$menu-bg-item-hover}]
  .icon
    @apply h-4 w-4
    @apply text-[#{$global-bg-icon-secondary}]
  .check
    @apply ml-auto
</style>
